import {
  useContext,
  useEffect,
  useMemo,
  ReactNode,
  useState,
  useCallback,
} from 'react';
import { Stack, Step, StepLabel, Stepper } from '@mui/material';
import UploadStep from './UploadStep';
import MapStep from './MapStep';
import PreviewStep, { CorrectionFormProps } from './PreviewStep';
import CompleteStep from './CompleteStep';
import { ImportWizardContext } from 'components/importer/ImportWizardContext';
import { useFetcher } from 'react-router';
import { useBackButton } from 'hooks/useBackButton';
import { useTranslation } from 'react-i18next';
import { ToastContext, TOAST_TYPE } from 'context/toastContext';
import {
  ArrowBack,
  ArrowForward,
  Cancel,
  Delete,
  Save,
  Add,
} from '@mui/icons-material';
import {
  SavedImportMapping,
  TraxeroActionResponse,
  FailedImport,
  ImportStep,
  WIZARD_ACTION,
  PermitType,
  Tenant,
  ImportWizardActionHandler,
} from 'types';
import { AppContext, ACTION_TYPE } from 'context/appContext';
import { ValidatorForm } from 'react-material-ui-form-validator';
import FormFrame from 'components/layout/FormFrame';
import DetailsHeader from 'components/layout/DetailsHeader';
import { MRT_ColumnDef } from 'material-react-table';

interface ImportWizardProps<T extends object> {
  // Navigation and Routing
  fetcherKey: string;
  actionRoute: string; // Route for form submission
  onBack?: () => void;
  maxHeight: number | string;
  // getRowId: (row: T | undefined, index: number) => string;

  previewColumns: MRT_ColumnDef<T>[];
  CorrectionForm: React.FC<CorrectionFormProps<T>>;
  UploadStepRightComponent?: React.ReactNode;

  // Permit Specific Configuration
  _permitType?: PermitType;

  // Property Specific Configuration
  _tenant?: Tenant;
}

const ImportWizard = <T extends object>({
  fetcherKey,
  actionRoute,
  previewColumns,
  CorrectionForm,
  UploadStepRightComponent,
  onBack,
  maxHeight,
  _permitType,
  _tenant,
}: ImportWizardProps<T>): ReactNode => {
  const { t } = useTranslation();
  const displayToast = useContext(ToastContext);
  const fetcher = useFetcher({ key: fetcherKey });
  const { state, dispatch, processEntities } = useContext(ImportWizardContext);
  const { state: appState, dispatch: appDispatch } = useContext(AppContext);
  const tenant = _tenant || appState.tenant;
  const [savedMapping, setSavedMapping] = useState<SavedImportMapping | null>(
    null,
  );

  // back button handling
  useBackButton(() => onBack?.());

  // step handling
  const activeStepIndex = useMemo(() => {
    return Object.values(ImportStep).indexOf(state.activeStep);
  }, [state.activeStep]);

  const renderStepContent = () => {
    switch (state.activeStep) {
      case ImportStep.UPLOAD:
        return <UploadStep rightColumn={UploadStepRightComponent} />;
      case ImportStep.MAP:
        return <MapStep maxHeight={maxHeight} />;
      case ImportStep.PREVIEW:
        return (
          <PreviewStep<T>
            maxHeight={maxHeight}
            columns={previewColumns}
            CorrectionForm={CorrectionForm}
          />
        );
      case ImportStep.COMPLETE:
        return <CompleteStep saveColumnMappings={saveColumnMappingsToServer} />;
    }
  };

  const getSuccessMessage = useCallback(
    (response: TraxeroActionResponse<ImportWizardActionHandler>) => {
      switch (response.handler) {
        case ImportWizardActionHandler.CREATE_ENTITY:
          return t('create_success', {
            ns: 'toasts',
            name: t(`${state.entityName}.label_plural`, { ns: 'entities' }),
            error: `${response.status}-${response.statusText}`,
          });
        case ImportWizardActionHandler.SUBMIT_ENTITY_DATA:
          return t('import_success', {
            ns: 'toasts',
            name: t(`${state.entityName}.label_plural`, { ns: 'entities' }),
            error: `${response.status}-${response.statusText}`,
          });
        case ImportWizardActionHandler.SAVE_COLUMN_MAPPINGS:
          return t('save_success', {
            ns: 'toasts',
            name: t('columnMappings', { ns: 'importWizard' }),
            error: `${response.status}-${response.statusText}`,
          });
        default:
          return t('toast.importSuccess', {
            name: t(`${state.entityName}.label_plural`, { ns: 'entities' }),
            error: `${response.status}-${response.statusText}`,
          });
      }
    },
    [t, state.entityName],
  );
  const getErrorMessage = useCallback(
    (response: TraxeroActionResponse<ImportWizardActionHandler>) => {
      switch (response.handler) {
        case ImportWizardActionHandler.CREATE_ENTITY:
          return t('create_error', {
            ns: 'toasts',
            name: t(`${state.entityName}.label`, { ns: 'entities' }),
            error: `${response.status}-${response.statusText}`,
          });
        case ImportWizardActionHandler.SUBMIT_ENTITY_DATA:
          return t('create_error', {
            ns: 'toasts',
            name: t(`${state.entityName}.label_plural`, { ns: 'entities' }),
            error: `${response.status}-${response.statusText}`,
          });
        case ImportWizardActionHandler.SAVE_COLUMN_MAPPINGS:
          if (response.status === 403) {
            return t('save_error', {
              ns: 'toasts',
              name: t('columnMappings', { ns: 'importWizard' }),
              error: t('saveMappingNameExists', { ns: 'importWizard' }),
            });
          }
          return t('save_error', {
            ns: 'toasts',
            name: t('columnMappings', { ns: 'importWizard' }),
            error: `${response.status}-${response.statusText}`,
          });
        default:
          return t('import_error', {
            ns: 'toasts',
            name: t(`${state.entityName}.label_plural`, { ns: 'entities' }),
            error: `${response.status}-${response.statusText}`,
          });
      }
    },
    [t, state.entityName],
  );

  // response handling
  const response =
    fetcher.data as TraxeroActionResponse<ImportWizardActionHandler>;
  useEffect(() => {
    if (response) {
      switch (response.handler) {
        case ImportWizardActionHandler.SAVE_COLUMN_MAPPINGS:
          handleSaveColumnMappingsResponse(response);
          break;
        case ImportWizardActionHandler.CREATE_ENTITY:
          handleCreateEntityResponse(response);
          break;
        case ImportWizardActionHandler.SUBMIT_ENTITY_DATA:
          handleDataImportResponse(response);
          break;
      }
    }
  }, [fetcher]);

  const handleDataImportResponse = useCallback(
    (response: TraxeroActionResponse<ImportWizardActionHandler>) => {
      if (response.status === 201) {
        // best case scenario, all entities were created successfully
        displayToast({
          type: TOAST_TYPE.SUCCESS,
          message: getSuccessMessage(response),
        });
        dispatch({ type: WIZARD_ACTION.HANDLE_NEXT });
      } else if (response.status === 200) {
        // entities were partially created, some failed
        const failedEntities = response.body as FailedImport<T>[];
        dispatch({
          type: WIZARD_ACTION.SET_FAILED_IMPORTS,
          payload: failedEntities,
        });
        displayToast({
          type: TOAST_TYPE.WARNING,
          message: t('import_partial_success', {
            ns: 'toasts',
            name: t(`${state.entityName}.label_plural`, { ns: 'entities' }),
            successCount:
              state.modifiedData.length -
              state.discardedRows.length -
              failedEntities.length,
            totalCount: state.modifiedData.length - state.discardedRows.length,
          }),
        });
      } else {
        displayToast({
          type: TOAST_TYPE.ERROR,
          message: getErrorMessage(response),
        });
      }
    },
    [
      displayToast,
      getSuccessMessage,
      getErrorMessage,
      dispatch,
      state.entityName,
      state.modifiedData,
      state.discardedRows,
      t,
    ],
  );

  const handleCreateEntityResponse = useCallback(
    (response: TraxeroActionResponse<ImportWizardActionHandler>) => {
      if (response.status < 400) {
        displayToast({
          type: TOAST_TYPE.SUCCESS,
          message: getSuccessMessage(response),
        });
        dispatch({ type: WIZARD_ACTION.POP_FAILED_IMPORT });
      } else {
        displayToast({
          type: TOAST_TYPE.ERROR,
          message: getErrorMessage(response),
        });
      }
    },
    [displayToast, getSuccessMessage, getErrorMessage, dispatch],
  );

  const handleSaveColumnMappingsResponse = useCallback(
    (response: TraxeroActionResponse<ImportWizardActionHandler>) => {
      if (response.status < 400) {
        displayToast({
          type: TOAST_TYPE.SUCCESS,
          message: getSuccessMessage(response),
        });
        if (savedMapping) {
          appDispatch({
            type: ACTION_TYPE.SET_ACTIVE_TENANT,
            payload: {
              ...appState.tenant,
              data: {
                ...appState.tenant.data,
                savedImportMappings: [
                  ...(appState.tenant.data.savedImportMappings || []),
                  savedMapping,
                ],
              },
            },
          });
          setSavedMapping(null);
        }
      } else {
        displayToast({
          type: TOAST_TYPE.ERROR,
          message: getErrorMessage(response),
        });
      }
    },
    [t, displayToast, appDispatch, savedMapping],
  );

  const handleCancel = useCallback(() => {
    if (state.activeStep === ImportStep.UPLOAD) {
      if (onBack) {
        onBack();
      }
    }
    if (state.activeStep === ImportStep.MAP) {
      dispatch({ type: WIZARD_ACTION.RESET_STATE });
    }
    if (state.activeStep === ImportStep.PREVIEW) {
      if (state.failedImports.length) {
        dispatch({
          type: WIZARD_ACTION.SET_DISCARDED_ROWS,
          payload: [...state.discardedRows, state.failedImports[0].index],
        });
        dispatch({ type: WIZARD_ACTION.POP_FAILED_IMPORT });
      } else {
        dispatch({ type: WIZARD_ACTION.HANDLE_BACK });
      }
    }
    if (state.activeStep === ImportStep.COMPLETE) {
      onBack?.();
    }
  }, [state.activeStep, state.failedImports.length, onBack, dispatch]);

  // button handling and state
  const handleSubmit = useCallback(() => {
    if (state.activeStep === ImportStep.UPLOAD) {
      dispatch({ type: WIZARD_ACTION.HANDLE_NEXT });
      dispatch({ type: WIZARD_ACTION.SET_TENANT, payload: tenant });
    }
    if (state.activeStep === ImportStep.MAP) {
      processEntities(state.modifiedData, state.columnMappings);
      dispatch({ type: WIZARD_ACTION.HANDLE_NEXT });
    }
    if (state.activeStep === ImportStep.PREVIEW && state.failedImports.length) {
      fetcher.submit(
        {
          handler: ImportWizardActionHandler.CREATE_ENTITY,
          body: JSON.stringify(state.failedImport),
          permitTypeID: _permitType?.id || 0,
          tenantID: tenant.id,
          systemID: tenant.systemID,
        },
        {
          method: 'POST',
          encType: 'application/json',
          action: actionRoute,
        },
      );
    } else if (
      state.activeStep === ImportStep.PREVIEW &&
      state.stringifiedEntities
    ) {
      fetcher.submit(
        {
          handler: ImportWizardActionHandler.SUBMIT_ENTITY_DATA,
          body: state.stringifiedEntities,
          permitTypeID: _permitType?.id || 0,
          tenantID: tenant.id,
          systemID: tenant.systemID,
        },
        {
          method: 'POST',
          encType: 'application/json',
        },
      );
    } else if (state.activeStep === ImportStep.COMPLETE && state.tenant) {
      dispatch({ type: WIZARD_ACTION.RESET_STATE });
    }
  }, [
    state.activeStep,
    state.failedImports.length,
    state.stringifiedEntities,
    state.modifiedData,
    state.columnMappings,
    processEntities,
    dispatch,
    tenant,
    actionRoute,
    _permitType,
  ]);

  const submitButtonProps = useMemo(() => {
    let label = '';
    let disabled = false;
    let icon: ReactNode = null;

    switch (state.activeStep) {
      case ImportStep.UPLOAD:
        label = t('buttons.upload_submit', { ns: 'importWizard' });
        disabled = !state.hasValidCsvData;
        icon = <ArrowForward />;
        break;
      case ImportStep.MAP:
        label = t('buttons.map_submit', { ns: 'importWizard' });
        disabled = !state.hasValidColumnMappings;
        icon = <ArrowForward />;
        break;
      case ImportStep.PREVIEW:
        label = state.failedImports.length
          ? t('buttons.create', {
              name: t(`${state.entityName.toLowerCase()}.label`, {
                ns: 'entities',
              }),
            })
          : t('buttons.import', {
              name: t(`${state.entityName.toLowerCase()}.label_plural`, {
                ns: 'entities',
              }),
            });
        disabled = false;
        icon = <Save />;
        break;
      case ImportStep.COMPLETE:
        label = t('buttons.complete_submit', {
          ns: 'importWizard',
          entity: t(`${state.entityName.toLowerCase()}.label_plural`, {
            ns: 'entities',
          }),
        });
        disabled = false;
        icon = <Add />;
        break;
    }

    return { label, disabled, icon };
  }, [
    state.activeStep,
    state.hasValidCsvData,
    state.hasValidColumnMappings,
    state.failedImports.length,
  ]);

  const cancelButtonProps = useMemo(() => {
    let label = '';
    let icon: ReactNode = null;

    switch (state.activeStep) {
      case ImportStep.UPLOAD:
        label = t('buttons.upload_cancel', {
          ns: 'importWizard',
          entity: t(`${state.entityName.toLowerCase()}.label_plural`, {
            ns: 'entities',
          }),
        });
        icon = <Cancel />;
        break;
      case ImportStep.MAP:
        label = t('buttons.map_cancel', { ns: 'importWizard' });
        icon = <ArrowBack />;
        break;
      case ImportStep.PREVIEW:
        if (state.failedImports.length) {
          label = t('buttons.discard_failed_import', { ns: 'importWizard' });
          icon = <Delete />;
        } else {
          label = t('buttons.preview_cancel', { ns: 'importWizard' });
          icon = <ArrowBack />;
        }
        break;
      case ImportStep.COMPLETE:
        label = t('buttons.upload_cancel', {
          ns: 'importWizard',
          entity: t(`${state.entityName}.label_plural`, { ns: 'entities' }),
        });
        icon = null;
        break;
    }

    return { label, icon };
  }, [state.activeStep, state.failedImports.length]);

  const saveColumnMappingsToServer = (mapping: SavedImportMapping) => {
    // check if the mapping name already exists
    const existingMappings = tenant.data.savedImportMappings || [];
    const existingMapping = existingMappings.find(
      (m) => m.name.toLowerCase().trim() === mapping.name.toLowerCase().trim(),
    );
    if (existingMapping) {
      displayToast({
        type: TOAST_TYPE.ERROR,
        message: t('save_error', {
          ns: 'toasts',
          name: t('columnMappings', { ns: 'importWizard' }),
          error: t('saveMappingNameExists', { ns: 'importWizard' }),
        }),
      });
      return;
    }

    setSavedMapping(mapping);
    fetcher.submit(
      {
        body: JSON.stringify(mapping),
        handler: ImportWizardActionHandler.SAVE_COLUMN_MAPPINGS,
        tenantID: tenant.id,
        systemID: tenant.systemID,
      },
      {
        method: 'POST',
        encType: 'application/json',
        action: actionRoute,
      },
    );
  };

  return (
    <ValidatorForm onSubmit={handleSubmit}>
      <FormFrame loading={fetcher.state !== 'idle'}>
        <DetailsHeader
          title={
            <Stepper
              activeStep={activeStepIndex}
              sx={{
                width: 800,
              }}
            >
              <Step>
                <StepLabel>
                  {t('steps.upload', { ns: 'importWizard' })}
                </StepLabel>
              </Step>
              <Step>
                <StepLabel>{t('steps.map', { ns: 'importWizard' })}</StepLabel>
              </Step>
              <Step>
                <StepLabel>
                  {t('steps.preview', { ns: 'importWizard' })}
                </StepLabel>
              </Step>
            </Stepper>
          }
          onCancel={handleCancel}
          cancelButtonLabel={cancelButtonProps.label}
          cancelButtonIcon={cancelButtonProps.icon}
          submitLabel={submitButtonProps.label}
          submitButtonIcon={submitButtonProps.icon}
          disableSubmit={submitButtonProps.disabled}
          hideCancel={!onBack && state.activeStep === ImportStep.UPLOAD}
        />
        <Stack sx={{ p: 2 }}>{renderStepContent()}</Stack>
      </FormFrame>
    </ValidatorForm>
  );
};

export default ImportWizard;
